import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

/* ============================================================
   LOGIN — Backoffice bot memecoin Solana (V1)
   Look = mockup design-docs/mockups/Login.jsx (dark terminal,
   full-screen, CSS scopé inline sous .au — pattern CockpitPage).
   Comportement = vraie auth (AuthContext.login → navigate('/')).
   ============================================================ */

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600;700;800&display=swap');
.au{
  --bg:#0a0b0e;--surface:#14161c;--surface2:#191c23;--border:#1f2329;
  --text:#e6e8ec;--text2:#8a8f98;
  --green:#00e08a;--red:#ff3b5c;--amber:#ffb020;--cyan:#22d3ee;
}
.au,.au *{font-family:'JetBrains Mono',ui-monospace,monospace;box-sizing:border-box;margin:0;padding:0;}
.au{background:var(--bg);color:var(--text);height:100vh;width:100%;overflow:hidden;
  display:flex;align-items:center;justify-content:center;position:relative;
  -webkit-font-smoothing:antialiased;font-size:13px;}

/* atmosphere : grille + glow + scanlines */
.au .grid{position:absolute;inset:0;pointer-events:none;
  background-image:linear-gradient(#ffffff05 1px,transparent 1px),
    linear-gradient(90deg,#ffffff05 1px,transparent 1px);
  background-size:46px 46px;animation:au-gridmove 24s linear infinite;}
@keyframes au-gridmove{to{background-position:46px 46px;}}
.au .glow{position:absolute;top:-10%;left:50%;transform:translateX(-50%);width:560px;height:560px;
  background:radial-gradient(circle,rgba(255,59,92,.16),transparent 62%);pointer-events:none;
  animation:au-glowp 5s ease-in-out infinite;}
@keyframes au-glowp{0%,100%{opacity:.7;}50%{opacity:1;}}
.au .scan{position:absolute;inset:0;pointer-events:none;
  background:repeating-linear-gradient(0deg,transparent,transparent 3px,#00000022 3px,#00000022 4px);
  opacity:.35;}
.au .vig{position:absolute;inset:0;pointer-events:none;
  background:radial-gradient(circle at 50% 40%,transparent 40%,#000 130%);}

/* statut système (haut) */
.au .sysbar{position:absolute;top:0;left:0;right:0;display:flex;align-items:center;gap:18px;
  padding:11px 18px;font-size:10.5px;color:var(--text2);letter-spacing:.05em;z-index:3;
  border-bottom:1px solid var(--border);background:#0c0d11cc;}
.au .sysbar .on{color:var(--green);display:flex;align-items:center;gap:6px;}
.au .sysbar .d{width:7px;height:7px;border-radius:50%;background:var(--green);animation:au-pulseg 2s infinite;}
@keyframes au-pulseg{0%{box-shadow:0 0 0 0 rgba(0,224,138,.5);}70%{box-shadow:0 0 0 6px rgba(0,224,138,0);}100%{box-shadow:0 0 0 0 rgba(0,224,138,0);}}
.au .sysbar .sp{flex:1;}
.au .sysbar b{color:var(--text);font-weight:600;}

/* card */
.au .card{position:relative;z-index:4;width:404px;max-width:92vw;background:var(--surface);
  border:1px solid var(--border);border-radius:16px;padding:30px 30px 22px;
  box-shadow:0 30px 80px rgba(0,0,0,.55),0 0 0 1px #ffffff05 inset;}
.au .card.shake{animation:au-shake .4s;}
@keyframes au-shake{0%,100%{transform:translateX(0);}20%,60%{transform:translateX(-7px);}40%,80%{transform:translateX(7px);}}

.au .brand{display:flex;align-items:center;gap:12px;margin-bottom:6px;
  opacity:0;animation:au-up .5s .05s forwards;}
.au .logo{width:42px;height:42px;border-radius:11px;background:var(--red);color:#fff;display:flex;
  align-items:center;justify-content:center;font-weight:800;font-size:21px;
  box-shadow:0 0 22px rgba(255,59,92,.5);}
.au .brand .wm{font-size:19px;font-weight:800;letter-spacing:.02em;}
.au .brand .sub{font-size:10px;color:var(--text2);letter-spacing:.16em;text-transform:uppercase;margin-top:1px;}

.au .modechip{display:inline-flex;align-items:center;gap:7px;font-size:10px;font-weight:800;
  letter-spacing:.06em;color:var(--red);background:rgba(255,59,92,.12);
  border:1px solid rgba(255,59,92,.4);padding:5px 11px;border-radius:20px;margin:16px 0 4px;
  opacity:0;animation:au-up .5s .12s forwards;}
.au .modechip .d{width:7px;height:7px;border-radius:50%;background:var(--red);animation:au-pulser 1.2s infinite;}
@keyframes au-pulser{0%,100%{opacity:1;}50%{opacity:.35;}}

/* boot log */
.au .boot{margin:14px 0 20px;font-size:10.5px;line-height:1.7;color:var(--text2);
  border-left:2px solid var(--border);padding-left:11px;min-height:54px;}
.au .boot div{opacity:0;animation:au-up .35s forwards;}
.au .boot .ok{color:var(--green);}
.au .boot div:nth-child(1){animation-delay:.35s;}
.au .boot div:nth-child(2){animation-delay:.7s;}
.au .boot div:nth-child(3){animation-delay:1.05s;}

@keyframes au-up{from{opacity:0;transform:translateY(7px);}to{opacity:1;transform:translateY(0);}}

/* fields */
.au .field{margin-bottom:14px;opacity:0;animation:au-up .5s forwards;}
.au .field:nth-of-type(1){animation-delay:1.2s;}
.au .field:nth-of-type(2){animation-delay:1.32s;}
.au .flabel{font-size:9.5px;letter-spacing:.13em;text-transform:uppercase;color:var(--text2);
  font-weight:600;margin-bottom:6px;display:flex;justify-content:space-between;align-items:center;}
.au .flabel .toggle{color:var(--cyan);cursor:pointer;letter-spacing:.04em;text-transform:none;font-weight:500;}
.au .flabel .toggle:hover{text-decoration:underline;}
.au .inp{width:100%;background:#0a0b0e;border:1px solid var(--border);border-radius:9px;
  padding:12px 13px;color:var(--text);font-size:14px;font-family:inherit;outline:none;
  transition:border-color .15s,box-shadow .15s;letter-spacing:.02em;}
.au .inp::placeholder{color:#4d525b;}
.au .inp:focus{border-color:var(--cyan);box-shadow:0 0 0 3px rgba(34,211,238,.12);}
.au .inp.err{border-color:var(--red);box-shadow:0 0 0 3px rgba(255,59,92,.12);}
.au .capslock{font-size:10px;color:var(--amber);margin-top:6px;display:flex;align-items:center;gap:5px;}

.au .errbox{background:rgba(255,59,92,.1);border:1px solid rgba(255,59,92,.4);color:var(--red);
  font-size:11.5px;padding:9px 12px;border-radius:8px;margin-bottom:14px;display:flex;
  align-items:center;gap:8px;animation:au-up .25s;}

.au .submit{width:100%;background:var(--green);color:#04150d;border:none;border-radius:10px;
  padding:13px;font-weight:800;font-size:13.5px;letter-spacing:.04em;cursor:pointer;
  font-family:inherit;display:flex;align-items:center;justify-content:center;gap:9px;
  box-shadow:0 0 22px rgba(0,224,138,.28);transition:filter .12s,transform .1s;
  opacity:0;animation:au-up .5s 1.44s forwards;}
.au .submit:hover{filter:brightness(1.08);}
.au .submit:active{transform:translateY(1px);}
.au .submit:disabled{opacity:.6;cursor:wait;filter:none;}
.au .spin{width:14px;height:14px;border:2px solid #04150d44;border-top-color:#04150d;
  border-radius:50%;animation:au-rot .7s linear infinite;}
@keyframes au-rot{to{transform:rotate(360deg);}}

.au .foot{margin-top:18px;display:flex;justify-content:space-between;align-items:center;
  font-size:10px;color:#5a5f68;opacity:0;animation:au-up .5s 1.56s forwards;}
.au .lock{display:flex;align-items:center;gap:5px;}

/* success */
.au .ok-overlay{position:absolute;inset:0;z-index:20;background:#0a0b0eee;backdrop-filter:blur(4px);
  display:flex;flex-direction:column;align-items:center;justify-content:center;gap:18px;animation:au-up .3s;}
.au .ok-ring{width:78px;height:78px;border-radius:50%;border:2px solid rgba(0,224,138,.3);
  display:flex;align-items:center;justify-content:center;position:relative;}
.au .ok-ring::after{content:"";position:absolute;inset:-2px;border-radius:50%;
  border:2px solid transparent;border-top-color:var(--green);animation:au-rot 1s linear infinite;}
.au .ok-check{font-size:34px;color:var(--green);}
.au .ok-t{font-size:15px;font-weight:800;color:var(--green);letter-spacing:.03em;}
.au .ok-s{font-size:11px;color:var(--text2);letter-spacing:.05em;}
`;

function LoginPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [caps, setCaps] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [shake, setShake] = useState(false);
  const userRef = useRef(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const t = setTimeout(() => userRef.current && userRef.current.focus(), 1300);
    return () => clearTimeout(t);
  }, []);

  const checkCaps = (e) => setCaps(e.getModifierState && e.getModifierState("CapsLock"));

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (loading || success) return;
    setError("");
    if (!username.trim() || !password) {
      setError("Identifiant et mot de passe requis.");
      setShake(true);
      setTimeout(() => setShake(false), 420);
      return;
    }
    setLoading(true);
    try {
      await login(username, password);
      setSuccess(true);
      navigate("/");
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed");
      setShake(true);
      setTimeout(() => setShake(false), 420);
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => {
    checkCaps(e);
    if (e.key === "Enter") handleSubmit();
  };

  return (
    <div className="au">
      <style>{CSS}</style>
      <div className="grid" />
      <div className="glow" />
      <div className="scan" />
      <div className="vig" />

      <div className="sysbar">
        <span className="on"><span className="d" /> SYSTÈME EN LIGNE</span>
        <span>4 / 4 services</span>
        <span>latence <b>7 ms</b></span>
        <span className="sp" />
        <span>région <b>eu-west · ovh</b></span>
        <span>build <b>v1.0.0</b></span>
      </div>

      <form className={`card ${shake ? "shake" : ""}`} onSubmit={handleSubmit}>
        <div className="brand">
          <div className="logo">N</div>
          <div>
            <div className="wm">NOVERIS</div>
            <div className="sub">Trading Control</div>
          </div>
        </div>

        <div className="modechip"><span className="d" /> BOT EN MODE LIVE · CAPITAL RÉEL</div>

        <div className="boot">
          <div>› établissement liaison sécurisée TLS… <span className="ok">OK</span></div>
          <div>› vérification du nœud Solana RPC… <span className="ok">OK</span></div>
          <div>› authentification requise — accès restreint</div>
        </div>

        {error && <div className="errbox">⚠ {error}</div>}

        <div className="field">
          <div className="flabel">Identifiant</div>
          <input
            ref={userRef}
            className={`inp ${error && !username.trim() ? "err" : ""}`}
            placeholder="operator"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            onKeyUp={onKey}
            autoComplete="username"
          />
        </div>

        <div className="field">
          <div className="flabel">
            Mot de passe
            <span className="toggle" onClick={() => setShowPw(!showPw)}>
              {showPw ? "masquer" : "afficher"}
            </span>
          </div>
          <input
            className={`inp ${error && !password ? "err" : ""}`}
            type={showPw ? "text" : "password"}
            placeholder="••••••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyUp={onKey}
            autoComplete="current-password"
          />
          {caps && <div className="capslock">⇪ Verr. Maj activé</div>}
        </div>

        <button className="submit" type="submit" disabled={loading}>
          {loading ? (<><span className="spin" /> Authentification…</>) : (<>Se connecter →</>)}
        </button>

        <div className="foot">
          <span className="lock">🔒 connexion journalisée</span>
          <span>secure session · solana sniper</span>
        </div>
      </form>

      {success && (
        <div className="ok-overlay">
          <div className="ok-ring"><span className="ok-check">✓</span></div>
          <div style={{ textAlign: "center" }}>
            <div className="ok-t">ACCÈS AUTORISÉ</div>
            <div className="ok-s">redirection vers le cockpit…</div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LoginPage;
